import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Building2, Calendar, Globe2, Star, ArrowRight, Filter, Search, Zap } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── STATIC CITY DATA (fallback base) ── */
const STATIC_CITIES = [
  { slug: 'istanbul',  name: 'İstanbul',  country: 'Türkiye',  flag: '🇹🇷', region: 'middle-east', tagline: 'Köprü Şehir — Doğu ile Batı\'nın buluşma noktası', image: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?q=80&w=800&auto=format&fit=crop', contractors: 220, events: 120, majorEvents: ['WIN Eurasia', 'Automechanika İstanbul', 'Foodex'], color: 'from-red-600 to-red-800' },
  { slug: 'dubai',     name: 'Dubai',     country: 'BAE',       flag: '🇦🇪', region: 'middle-east', tagline: 'Küresel ticaretin yükselen yıldızı', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=800&auto=format&fit=crop', contractors: 200, events: 150, majorEvents: ['GITEX', 'Arab Health', 'Gulfood'], color: 'from-amber-500 to-orange-700' },
  { slug: 'london',    name: 'London',    country: 'İngiltere', flag: '🇬🇧', region: 'europe',      tagline: 'Avrupa\'nın finans ve inovasyon kalbi', image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=800&auto=format&fit=crop', contractors: 280, events: 180, majorEvents: ['Bett Show', 'London Book Fair', 'DSEI'], color: 'from-blue-700 to-blue-900' },
  { slug: 'paris',     name: 'Paris',     country: 'Fransa',    flag: '🇫🇷', region: 'europe',      tagline: 'Moda, lüks ve iş dünyasının başkenti', image: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?q=80&w=800&auto=format&fit=crop', contractors: 240, events: 160, majorEvents: ['Paris Air Show', 'SIAL', 'Maison & Objet'], color: 'from-indigo-600 to-purple-800' },
  { slug: 'berlin',    name: 'Berlin',    country: 'Almanya',   flag: '🇩🇪', region: 'europe',      tagline: 'Teknoloji ve yaratıcı sektörün merkezi', image: 'https://images.unsplash.com/photo-1560969184-10fe8719e047?q=80&w=800&auto=format&fit=crop', contractors: 190, events: 100, majorEvents: ['IFA', 'ITB Berlin', 'InnoTrans'], color: 'from-gray-700 to-gray-900' },
  { slug: 'frankfurt', name: 'Frankfurt', country: 'Almanya',   flag: '🇩🇪', region: 'europe',      tagline: 'Dünyanın en büyük fuar kompleksine ev sahipliği', image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?q=80&w=800&auto=format&fit=crop', contractors: 210, events: 50, majorEvents: ['Automechanika', 'Frankfurt Book Fair', 'Ambiente'], color: 'from-emerald-600 to-teal-800' },
  { slug: 'milan',     name: 'Milan',     country: 'İtalya',    flag: '🇮🇹', region: 'europe',      tagline: 'Made in Italy — tasarım ve modanın başkenti', image: 'https://images.unsplash.com/photo-1520175480921-4edfa2983e0f?q=80&w=800&auto=format&fit=crop', contractors: 170, events: 80, majorEvents: ['Salone del Mobile', 'EICMA', 'Host Milano'], color: 'from-rose-600 to-pink-800' },
  { slug: 'amsterdam', name: 'Amsterdam', country: 'Hollanda',  flag: '🇳🇱', region: 'europe',      tagline: 'Hollanda tasarımı ile sürdürülebilir fuar deneyimi', image: 'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?q=80&w=800&auto=format&fit=crop', contractors: 150, events: 60, majorEvents: ['ISE', 'Horecava', 'Interclean'], color: 'from-orange-500 to-red-700' },
  { slug: 'barcelona', name: 'Barcelona', country: 'İspanya',   flag: '🇪🇸', region: 'europe',      tagline: 'Katalan yaratıcılığı ile tech ve gastronomi buluşuyor', image: 'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?q=80&w=800&auto=format&fit=crop', contractors: 160, events: 70, majorEvents: ['Mobile World Congress', 'Alimentaria', 'Smart City Expo'], color: 'from-yellow-500 to-orange-600' },
  { slug: 'hannover',  name: 'Hannover',  country: 'Almanya',   flag: '🇩🇪', region: 'europe',      tagline: 'Dünyanın en büyük fuar tesisi — endüstrinin kalbi', image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?q=80&w=800&auto=format&fit=crop', contractors: 230, events: 40, majorEvents: ['Hannover Messe', 'Agritechnica', 'EMO'], color: 'from-sky-600 to-blue-800' },
  { slug: 'beijing',   name: 'Pekin',     country: 'Çin',       flag: '🇨🇳', region: 'asia',        tagline: 'Çin\'in siyasi ve ekonomik başkenti', image: 'https://images.unsplash.com/photo-1508804185872-d7badad00f7d?q=80&w=800&auto=format&fit=crop', contractors: 150, events: 90, majorEvents: ['China International Fair', 'Auto China', 'CIIF'], color: 'from-red-700 to-yellow-600' },
];

function mergeCities(staticCities, dbCities) {
  const dbSlugs = new Set(dbCities.map(c => c.slug));
  const staticOnly = staticCities.filter(c => !dbSlugs.has(c.slug));
  const dbMapped = dbCities.map(c => ({
    slug: c.slug, name: c.name, country: c.country || '', flag: c.flag || '🌍',
    region: c.region || 'other', tagline: c.tagline || '',
    image: c.image || '', contractors: c.contractors_count || 0, events: 0,
    majorEvents: [], color: 'from-blue-600 to-blue-900', autoCreated: c.auto_created || false,
  }));
  return [...dbMapped, ...staticOnly];
}

/* ── STAT BADGE ── */
function StatBadge({ value, label, icon: Icon }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-blue-400" />
        <span className="text-3xl font-black text-white">{typeof value === 'number' ? value.toLocaleString() : value}+</span>
      </div>
      <span className="text-xs text-blue-200 uppercase tracking-widest font-medium">{label}</span>
    </div>
  );
}

/* ── CITY CARD ── */
function CityCard({ city, index }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.04 }}
    >
      <Link
        to={`/city/${city.slug}`}
        data-testid={`city-card-${city.slug}`}
        className="group block relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 h-72 cursor-pointer"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <img src={city.image} alt={city.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className={`absolute inset-0 bg-gradient-to-t ${city.color} opacity-60 group-hover:opacity-75 transition-opacity duration-500`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

        <div className="absolute top-3 right-3 flex items-center gap-1.5">
          {city.autoCreated && (
            <span className="bg-emerald-500/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1">
              <Zap className="h-2.5 w-2.5" />YENİ
            </span>
          )}
          <span className="text-xl" title={city.country}>{city.flag}</span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className={`transition-all duration-500 ${hovered ? 'mb-2' : 'mb-0'}`}>
            <h3 className="text-xl font-black text-white leading-tight">{city.name}</h3>
            <p className="text-xs text-white/70 font-medium">{city.country}</p>
          </div>
          <AnimatePresence>
            {hovered && (
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }} transition={{ duration: 0.2 }}
                className="space-y-2"
              >
                {city.tagline && <p className="text-xs text-white/80 italic leading-relaxed">{city.tagline}</p>}
                <div className="flex items-center gap-3 text-xs">
                  {city.contractors > 0 && (
                    <span className="flex items-center gap-1 text-white/90">
                      <Building2 className="h-3 w-3" />{city.contractors} yüklenici
                    </span>
                  )}
                  {city.events > 0 && (
                    <span className="flex items-center gap-1 text-white/90">
                      <Calendar className="h-3 w-3" />{city.events} fuar/yıl
                    </span>
                  )}
                </div>
                {city.majorEvents?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {city.majorEvents.slice(0, 2).map((e, i) => (
                      <span key={i} className="text-[10px] bg-white/20 backdrop-blur-sm text-white px-2 py-0.5 rounded-full font-medium">{e}</span>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          <div className={`flex items-center gap-1 mt-2 transition-all duration-300 ${hovered ? 'opacity-100 translate-x-1' : 'opacity-0'}`}>
            <span className="text-xs text-white font-semibold">Keşfet</span>
            <ArrowRight className="h-3.5 w-3.5 text-white" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

/* ── MAIN PAGE ── */
export default function LocationsPage() {
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('all');
  const [allCities, setAllCities] = useState(STATIC_CITIES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/public/cities`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.cities) {
          setAllCities(mergeCities(STATIC_CITIES, data.cities));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const REGIONS = [
    { key: 'all',         label: 'Tüm Şehirler', count: allCities.length },
    { key: 'europe',      label: 'Avrupa',        count: allCities.filter(c => c.region === 'europe').length },
    { key: 'middle-east', label: 'Orta Doğu',     count: allCities.filter(c => c.region === 'middle-east').length },
    { key: 'asia',        label: 'Asya',           count: allCities.filter(c => c.region === 'asia').length },
    { key: 'other',       label: 'Diğer',          count: allCities.filter(c => !['europe','middle-east','asia'].includes(c.region)).length },
  ].filter(r => r.count > 0);

  const total = {
    contractors: allCities.reduce((s, c) => s + (c.contractors || 0), 0),
    events:      allCities.reduce((s, c) => s + (c.events || 0), 0),
    countries:   new Set(allCities.map(c => c.country)).size,
  };

  const filtered = allCities.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.name.toLowerCase().includes(q) || c.country.toLowerCase().includes(q)
      || (c.majorEvents || []).some(e => e.toLowerCase().includes(q));
    const matchRegion = region === 'all' || c.region === region;
    return matchSearch && matchRegion;
  });

  return (
    <>
      <Helmet>
        <title>Fuar Şehirleri | ExpoStand — Global Stand Yapım Platformu</title>
        <meta name="description" content={`İstanbul'dan Dubai'ye, Hannover'dan Barcelona'ya ${allCities.length} küresel fuar şehrinde doğrulanmış stand contractor'larıyla çalışın.`} />
      </Helmet>

      {/* HERO */}
      <section className="relative bg-[#0a0f1e] overflow-hidden min-h-[400px] flex items-center">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(59,130,246,0.12) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="absolute left-1/3 top-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/20 rounded-full blur-[80px] pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 py-16 w-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 mb-6">
              <Globe2 className="h-4 w-4 text-blue-400" />
              <span className="text-xs font-semibold text-blue-300 uppercase tracking-widest">Global Fuar Ağı</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
              Dünyaca Ünlü<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">Fuar Şehirleri</span>
            </h1>
            <p className="text-lg text-blue-100/70 max-w-xl mb-10">
              İstanbul'dan Dubai'ye, Hannover'dan Barcelona'ya — <strong className="text-white">{allCities.length} küresel şehirde</strong> doğrulanmış stand contractor ağı.
            </p>
            <div className="flex flex-wrap gap-10">
              <StatBadge value={total.contractors} label="Doğrulanmış Contractor" icon={Building2} />
              <StatBadge value={total.events}      label="Yıllık Fuar"           icon={Calendar}   />
              <StatBadge value={total.countries}   label="Ülke"                  icon={MapPin}      />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
        <div className="mx-auto max-w-7xl px-6 py-3 flex flex-col sm:flex-row items-center gap-3">
          <div className="relative flex-1 w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text" placeholder="Şehir, ülke veya fuar ara..." value={search}
              onChange={e => setSearch(e.target.value)} data-testid="city-search-input"
              className="w-full pl-9 pr-4 py-2 text-sm rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-300 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter className="h-4 w-4 text-gray-400 shrink-0" />
            {REGIONS.map(r => (
              <button key={r.key} onClick={() => setRegion(r.key)} data-testid={`region-filter-${r.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${region === r.key ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {r.label} <span className="opacity-60">({r.count})</span>
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-400 ml-auto shrink-0">{filtered.length} şehir</span>
        </div>
      </section>

      {/* CITY GRID */}
      <section className="bg-gray-50 py-12">
        <div className="mx-auto max-w-7xl px-6">
          <AnimatePresence mode="wait">
            {filtered.length > 0 ? (
              <motion.div key={`${region}-${search}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filtered.map((city, i) => <CityCard key={city.slug} city={city} index={i} />)}
              </motion.div>
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-24">
                <Globe2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">"{search}" için şehir bulunamadı</p>
                <button onClick={() => { setSearch(''); setRegion('all'); }} className="mt-4 text-sm text-blue-600 underline">
                  Filtreleri temizle
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-blue-700 to-blue-900 py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl font-black text-white mb-3">Şehrinizi bulamadınız mı?</h2>
          <p className="text-blue-100 mb-8">Her ay yeni şehirler ekleniyor. Şu an listemizde olmayan bir şehir için de brief oluşturabilirsiniz.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="/exhibitor/signup" className="bg-white text-blue-700 font-bold px-8 py-3 rounded-xl shadow hover:shadow-lg transition-all hover:-translate-y-0.5">
              Ücretsiz Brief Oluştur
            </a>
            <a href="/exhibitor/landing" className="text-white border border-white/30 hover:bg-white/10 font-semibold px-8 py-3 rounded-xl transition-all flex items-center gap-2">
              Platform Hakkında <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
